import { useState } from "react";
import { Heart } from "lucide-react";
import { CloudDivider } from "./CloudDivider";
import { SectionClouds } from "./CloudCanvas";
import { useScrollReveal } from "../hooks/useScrollReveal";

// 3D-tilted polaroid
const TiltPolaroid = ({ children, baseRotation = -4, className = "" }) => {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({ rx: -(y - 0.5) * 18, ry: (x - 0.5) * 18 });
  };
  return (
    <div
      className={`polaroid ${className}`}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
      style={{
        transform: `perspective(1000px) rotate(${baseRotation}deg) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
        transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
    >
      {children}
    </div>
  );
};

export const Story = () => {
  const { ref, visible } = useScrollReveal();

  return (
    <section
      id="histoire"
      data-testid="story-section"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(255,250,236,0.85) 0%, rgba(255,245,214,0.85) 100%)",
      }}
    >
      <CloudDivider position="top" color="rgba(251,247,242,0.8)" />
      <CloudDivider position="bottom" color="rgba(251,247,242,0.8)" flip />
      <SectionClouds
        clouds={[
          { top: "8%", left: "-5%", size: 240, opacity: 0.85, blur: 26, duration: 14 },
          { top: "70%", right: "-3%", size: 200, opacity: 0.75, blur: 22, duration: 16, delay: 2 },
          { top: "55%", left: "5%", size: 160, opacity: 0.6, blur: 18, duration: 12, delay: 1 },
        ]}
      />

      <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 lg:gap-20 items-center" style={{ perspective: "1500px" }}>
        <div
          className="relative transition-all duration-1000"
          style={{
            opacity: visible ? 1 : 0,
            transform: `translateY(${visible ? 0 : 40}px)`,
          }}
        >
          <TiltPolaroid baseRotation={-4} className="max-w-sm anim-float-slow">
            <div className="bg-[#ffe6ec] rounded-sm flex items-center justify-center p-2">
              <img
                src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/xrhmy50c_photo-duo-amyu.jpeg"
                alt="Le duo fondateur Amyu"
                className="w-full h-80 object-contain mix-blend-multiply"
              />
            </div>
            <p className="font-hand text-2xl text-amyu-ink mt-3 text-center">Le duo Amyu ♡</p>
          </TiltPolaroid>
          <div className="absolute -bottom-8 -right-4 md:right-12 z-10">
            <TiltPolaroid baseRotation={5} className="max-w-[200px] anim-float-mid">
              <img
                src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/ew6c7qzw_tira-fraise-pistache.JPG"
                alt="Tiramisu Fraise Pistache Amyu"
                className="w-full h-32 object-cover"
              />
            </TiltPolaroid>
          </div>
        </div>

        <div
          data-testid="story-text"
          className="transition-all duration-1000"
          style={{
            opacity: visible ? 1 : 0,
            transform: `translateY(${visible ? 0 : 40}px)`,
            transitionDelay: "200ms",
          }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">Notre histoire</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05]">
            Tout a commencé dans
            <br />
            <span className="italic text-coral">une cuisine, à deux.</span>
          </h2>
          <div className="mt-6 space-y-4 text-amyu-muted leading-relaxed text-base md:text-lg">
            <p>
              Amyu est née d'une envie simple : partager des moments doux à travers des
              gourmandises faites maison, sans compromis sur le goût ni sur le plaisir.
            </p>
            <p>
              On a commencé avec quelques tiramisus, partagés à la famille et aux amis.
              Aujourd'hui, on cuisine pour une communauté qui grandit chaque semaine — et
              pour ça, on vous dit <span className="font-hand text-2xl text-coral">merci</span>.
            </p>
            <p>
              Chez Amyu, chaque création raconte une histoire : celle d'une rencontre,
              d'un voyage, d'un souvenir d'enfance ou simplement d'un coup de cœur food.
            </p>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 bg-white pill px-5 py-3 shadow-sm border border-amyu">
            <Heart size={16} className="text-coral fill-coral" />
            <span className="text-sm text-amyu-ink">100% fait maison, avec amour</span>
          </div>
        </div>
      </div>
    </section>
  );
};
